// frontend/src/pages/SellerDashboard.jsx
import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { Link } from 'react-router-dom';
import { 
  Package, ShoppingBag, TrendingUp, DollarSign, 
  Loader2, Eye, CheckCircle, Clock, XCircle, Box 
} from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import api from '../services/api';

export const SellerDashboard = () => {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalPackages: 0,
    totalOrders: 0,
    totalSales: 0,
    pendingOrders: 0,
    completedOrders: 0,
    recentOrders: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      
      // دریافت محصولات فروشنده
      const productsResponse = await api.get('/products/products/my-products/');
      const productsData = productsResponse.data;
      const productsList = productsData.results || productsData || [];
      
      // دریافت پکیج‌ها
      const packagesResponse = await api.get('/products/packages/');
      const packagesData = packagesResponse.data;
      const packagesList = packagesData.results || packagesData || [];
      
      // دریافت سفارشات مربوط به فروشنده
      const ordersResponse = await api.get('/orders/');
      const ordersData = ordersResponse.data;
      const allOrders = ordersData.results || ordersData || [];
      
      let totalSales = 0;
      let pendingOrders = 0;
      let completedOrders = 0;
      const recentOrders = [];
      
      allOrders.forEach(order => {
        const sellerItems = order.items?.filter(item => 
          item.product?.seller_id === user?.id || 
          item.product?.seller === user?.id
        );
        
        if (sellerItems && sellerItems.length > 0) {
          let orderSellerTotal = 0;
          sellerItems.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseInt(item.quantity) || 0;
            orderSellerTotal += price * quantity;
          });
          
          totalSales = Number(totalSales) + Number(orderSellerTotal);
          
          if (order.status === 'pending' || order.status === 'paid') {
            pendingOrders++;
          } else if (order.status === 'delivered' || order.status === 'completed') {
            completedOrders++;
          }
          
          if (recentOrders.length < 5) {
            recentOrders.push({
              ...order,
              sellerTotal: orderSellerTotal,
              sellerItemsCount: sellerItems.length
            });
          }
        }
      });
      
      setStats({
        totalProducts: productsList.length,
        totalPackages: packagesList.length,
        totalOrders: allOrders.filter(order => 
          order.items?.some(item => 
            item.product?.seller_id === user?.id || 
            item.product?.seller === user?.id
          )
        ).length,
        totalSales: totalSales,
        pendingOrders: pendingOrders,
        completedOrders: completedOrders,
        recentOrders: recentOrders
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '۰';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '۰';
    return numPrice.toLocaleString('fa-IR');
  };

  // ✅ تابع دریافت شماره سفارش
  const getOrderNumber = (order) => {
    return order.order_number || order.id?.toString().slice(0, 8) || '---';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار پرداخت', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'pending_confirmation': { text: 'در انتظار تایید پرداخت', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'paid': { text: 'پرداخت شده', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      'processing': { text: 'در حال پردازش', color: 'bg-purple-100 text-purple-800', icon: Clock },
      'shipped': { text: 'ارسال شده', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'delivered': { text: 'تحویل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'completed': { text: 'تکمیل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { text: 'لغو شده', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="داشبورد فروشنده | سان ایران" noindex={true} />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">داشبورد فروشنده</h1>
          
          {/* آمار کارت‌ها */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">کل محصولات</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
                </div>
                <Package className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-indigo-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">کل پکیج‌ها</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalPackages}</p>
                </div>
                <Box className="w-10 h-10 text-indigo-500 opacity-50" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">کل سفارشات</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
                </div>
                <ShoppingBag className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">سفارشات در انتظار</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border-r-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">فروش کل</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatPrice(stats.totalSales)} تومان
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-purple-500 opacity-50" />
              </div>
            </div>
          </div>
          
          {/* لینک‌های سریع */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Link
              to="/seller/products"
              className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-between hover:shadow-md transition group"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">مدیریت محصولات</h3>
                <p className="text-sm text-gray-500">افزودن، ویرایش و حذف محصولات</p>
              </div>
              <Package className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition" />
            </Link>
            
            <Link
              to="/seller/packages"
              className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-between hover:shadow-md transition group"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">مدیریت پکیج‌ها</h3>
                <p className="text-sm text-gray-500">ساخت، ویرایش و حذف پکیج‌های خورشیدی</p>
              </div>
              <Box className="w-8 h-8 text-gray-400 group-hover:text-indigo-500 transition" />
            </Link>
            
            <Link
              to="/seller/orders"
              className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-between hover:shadow-md transition group"
            >
              <div>
                <h3 className="font-bold text-gray-900 mb-1">مدیریت سفارشات</h3>
                <p className="text-sm text-gray-500">مشاهده و پردازش سفارشات</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition" />
            </Link>
          </div>
          
          {/* سفارشات اخیر */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">سفارشات اخیر</h2>
              <Link to="/seller/orders" className="text-sm text-blue-600 hover:text-blue-700">
                مشاهده همه
              </Link>
            </div>
            
            {stats.recentOrders.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                هیچ سفارشی یافت نشد
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {stats.recentOrders.map((order) => (
                  <div key={order.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {/* ✅ نمایش شماره سفارش با getOrderNumber */}
                          <span className="font-mono text-sm font-bold text-gray-900">
                            #{getOrderNumber(order)}
                          </span>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div className="text-gray-600">
                            <span className="font-medium">مشتری:</span> {order.user?.username || 'کاربر'}
                          </div>
                          <div className="text-gray-600">
                            <span className="font-medium">تاریخ:</span> {new Date(order.created_at).toLocaleDateString('fa-IR')}
                          </div>
                          <div className="text-gray-600">
                            <span className="font-medium">مبلغ سفارش (محصولات شما):</span> {formatPrice(order.sellerTotal)} تومان
                          </div>
                          <div className="text-gray-600">
                            <span className="font-medium">تعداد محصولات شما:</span> {order.sellerItemsCount} قلم
                          </div>
                        </div>
                      </div>
                      <Link
                        to={`/orders/${order.id}`}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        مشاهده
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};